import React from 'react';
import {
    Page,
    SkeletonBodyText,
    Card,
    Banner,
    Link,
    Select,
    TextField,
    Checkbox
} from '@shopify/polaris';
import OperationPreviewComponent from '../../components/OperationPreviewComponent';
import TagViewComponent from '../../components/TagViewComponent';
import { Helmet } from 'react-helmet';
import { apiFetch as fetch, ResourceTypes } from '../../common/Helpers';

class ScenarioChangePrefixPage extends React.Component {

    state = {
        step: null,
        resourceType: "Product",
        isLoading: true,
        tagsToMerge: [],
        targetTag: null,
        preview: null,
        originalPrefix: null,
        newPrefix: null,
        keepOriginalTags: false,
        inputValue: '',
        inputMatchingTags: [],
    };

    render() {

        if (this.state.step == null) {
            return (
                <div>
                    <Helmet>
                        <title>Tag Genie - Scenario - Change Prefix</title>
                    </Helmet>
                    <Page title="Change Prefix">
                        <SkeletonBodyText lines="20" />
                    </Page>
                </div>)
        }

        let resourcePlural = "products";
        let resourceSingular = "product";
        const tagsWithPrefix = this.getTagsWithPrefix(this.state.originalPrefix);
        const tagsWithNewPrefix = this.getTagsWithNewPrefix(tagsWithPrefix, this.state.originalPrefix, this.state.newPrefix);

        if (this.state.resourceType === "Customer") {
            resourcePlural = "customers";
            resourceSingular = "customer";
        }

        if (this.state.resourceType === "Order") {
            resourcePlural = "orders";
            resourceSingular = "order";
        }

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - Scenario - Change Prefix</title>
                </Helmet>
                {this.state.step === "create" &&
                    <Page title="Change Prefix">
                        <Card primaryFooterAction={{ content: 'Preview Change Prefix', onAction: this.handlePreview, loading: this.state.isLoading }}>
                            <Card.Section>
                                <Banner>
                                    <p>If you require more advanced functionality, consider using a <Link url="/operations/new/custom">Custom Tag Operation</Link>.
                                    </p>
                                </Banner>
                            </Card.Section>
                            <Card.Section title="Resource type">
                                <Select options={ResourceTypes} value={this.state.resourceType} onChange={this.onResourceChange} />
                            </Card.Section>
                            <Card.Section>
                                <TextField
                                    label="Prefix to change"
                                    placeholder="Enter prefix"
                                    helpText={
                                        <span>
                                            Choose the prefix that you would like to change. For example "brand_"
                                        </span>
                                    }
                                    value={this.state.originalPrefix}
                                    onChange={this.originalPrefixChange}
                                    error={this.state.originalPrefix != null && this.state.originalPrefix !== "" ? null : "Value is required"}
                                    autoComplete="off"
                                />
                                <div><br />Tags with original prefix:</div>
                                <TagViewComponent
                                    showFilter={false}
                                    showHighlight={false}
                                    showMarkUnmark={false}
                                    canSelect={false}
                                    tags={tagsWithPrefix} />
                                <TextField
                                    label="New prefix"
                                    placeholder="Enter prefix"
                                    helpText={
                                        <span>
                                            Choose the new prefix that will replace the old one. For example "product_brand_"
                                        </span>
                                    }
                                    value={this.state.newPrefix}
                                    onChange={this.newPrefixChange}
                                    error={this.state.newPrefix != null && this.state.newPrefix !== "" ? null : "Value is required"}
                                    autoComplete="off"
                                />
                                <div><br />Tags with new prefix:</div>
                                <TagViewComponent
                                    showFilter={false}
                                    showHighlight={false}
                                    showMarkUnmark={false}
                                    canSelect={false}
                                    tags={tagsWithNewPrefix} />
                                <br />
                                <Checkbox
                                    label="Keep original tags"
                                    checked={this.state.keepOriginalTags}
                                    onChange={this.keepOriginalTagsChange}
                                    helpText={
                                        <span>
                                            If checked, this option will allow you to retain all tags with the original prefix while will adding new tags with the new prefix.
                                            If unchecked, tags with the original prefix will be replaced with the new tags.
                                        </span>
                                    }
                                />
                            </Card.Section>
                        </Card>
                    </Page>
                }

                {this.state.step === "preview" &&
                    <Page title="Change Prefix - Preview">
                        <Card
                            title="Preview tag operation"
                            primaryFooterAction={{ content: "Execute Change Prefix Tags", disabled: !this.state.preview.canExecute || this.state.isLoading, onAction: this.handleSubmit, loading: this.state.isLoading }}
                            secondaryFooterActions={[{ content: 'Back', disabled: this.state.isLoading, onAction: this.handleBack }]}>
                            <OperationPreviewComponent
                                preview={this.state.preview}
                                resourcePlural={resourcePlural}
                                resourceSingular={resourceSingular} />
                        </Card>
                    </Page>}
            </div>
        );
    }

    handleAutocompleteSelect = (selected) => {
        if (selected.length <= 0) {
            this.setState({
                inputValue: '',
                targetTag: null
            });
            return;
        }

        this.setState({
            inputValue: selected[0],
            targetTag: selected[0]
        });
    }

    getTagsWithPrefix = (prefix) => {

        var tags = [];

        if (prefix == null || prefix === "") {
            return tags;
        }

        if (this.state.resourceType === "Product") {
            tags = this.state.data.tags;
        }

        if (this.state.resourceType === "Customer") {
            tags = this.state.data.customerTags;
        }

        if (this.state.resourceType === "Order") {
            tags = this.state.data.orderTags;
        }

        return tags.filter(_ => _.tagName.startsWith(prefix));
    }

    getTagsWithNewPrefix = (tagsWithPrefix, originalPrefix, newPrefix) => {

        if (newPrefix == null || newPrefix === "") {
            return [];
        }

        return tagsWithPrefix.map(_ => {
            return {
                tagName: _.tagName.replace(originalPrefix, newPrefix)
            };
        });
    }

    keepOriginalTagsChange = (value) => {
        this.setState({
            keepOriginalTags: value,
        }, () => this.updateText(''));
    }

    originalPrefixChange = (value) => {
        this.setState({
            originalPrefix: value,
        }, () => this.updateText(''));
    }

    newPrefixChange = (value) => {
        this.setState({
            newPrefix: value,
        }, () => this.updateText(''));
    }

    onResourceChange = (value) => {
        this.setState({
            resourceType: value,
        }, () => this.updateText(''));
    }

    updateText = (value) => {
        this.setState({
            inputValue: value,
            targetTag: value
        });
    }

    fetchDashboardData = () => {
        fetch('api/dashboard')
            .then(response => { return response.json() })
            .then(data => {
                this.setState({
                    data: data,
                    step: "create",
                    isLoading: false,
                }, () => { this.updateText('') });
            });
    }

    handlePreview = () => {

        this.setState({
            isLoading: true
        });

        fetch(`api/scenarios/changePrefix/preview`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                originalPrefix: this.state.originalPrefix,
                newPrefix: this.state.newPrefix,
                keepOriginalTags: this.state.keepOriginalTags,
                resourceType: this.state.resourceType
            })
        })
            .then(response => response.json())
            .then(data => {
                this.setState({
                    isLoading: false,
                    step: "preview",
                    preview: data
                });
                window.scrollTo(0, 0);
            });
    }

    handleSubmit = () => {
        this.setState({
            isLoading: true
        });

        fetch(`api/scenarios/changePrefix/execution`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                originalPrefix: this.state.originalPrefix,
                newPrefix: this.state.newPrefix,
                keepOriginalTags: this.state.keepOriginalTags,
                resourceType: this.state.resourceType
            })
        }).then(_ => {
            this.props.history.push("/operations");
        });
    }

    handleBack = async () => {
        this.setState({
            step: "create"
        })
    }

    componentDidMount() {
        this.fetchDashboardData();
    }
}

export default ScenarioChangePrefixPage;